import AttachMoneyIcon from '@material-design-icons/svg/filled/attach_money.svg?react';
import CloudUploadIcon from '@material-design-icons/svg/filled/cloud_upload.svg?react';
import KeyIcon from '@material-design-icons/svg/filled/key.svg?react';
import LockIcon from '@material-design-icons/svg/filled/lock.svg?react';
import SettingsIcon from '@material-design-icons/svg/filled/settings.svg?react';
import { delay } from 'es-toolkit/promise';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { usePreferences } from '../../contexts/PreferencesContext';
import { captureAndReportError } from '../../utils/errorReporting';
import { isUploadProviderEnabled } from '../../utils/providerHelpers';
import { hasS3Config } from '../../utils/s3CredentialManager';
import { LoadingIcon } from '../LoadingIcon';
import { TooltipProvider } from '../ui/tooltip';
import { providers } from './providers';
import type { ProviderType } from './providers/types';
export const PreferencesSettingsPage = () => {
  const { preferences, updatePreferences, isLoading, error } = usePreferences();
  const [selectedProvider, setSelectedProvider] = useState<ProviderType | null>(
    isUploadProviderEnabled(preferences.uploadProvider)
      ? preferences.uploadProvider
      : null,
  );
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [providerConfigs, setProviderConfigs] = useState({
    s3: false,
  });

  useEffect(() => {
    setSelectedProvider(
      isUploadProviderEnabled(preferences.uploadProvider)
        ? preferences.uploadProvider
        : null,
    );
  }, [preferences.uploadProvider]);

  useEffect(() => {
    setHasChanges(
      selectedProvider !== null &&
        selectedProvider !== preferences.uploadProvider,
    );
  }, [selectedProvider, preferences.uploadProvider]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    const checkConfigs = () => {
      setProviderConfigs({
        s3: hasS3Config(),
      });
    };

    checkConfigs();
    // Re-check on focus in case config was added in another tab
    const handleFocus = () => checkConfigs();
    window.addEventListener('focus', handleFocus);

    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const handleProviderChange = (provider: ProviderType) => {
    setSelectedProvider(provider);
  };

  const handleSave = async () => {
    if (!hasChanges || selectedProvider === null) return;

    if (selectedProvider === 's3' && !hasS3Config()) {
      toast.error('Please configure AWS S3 before saving');
      return;
    }

    const saveStartTime = Date.now();

    setIsSaving(true);
    try {
      await updatePreferences({ uploadProvider: selectedProvider });

      const elapsedTime = Date.now() - saveStartTime;
      const minimumSpinnerTime = 1000;
      const remainingTime = Math.max(0, minimumSpinnerTime - elapsedTime);

      if (remainingTime > 0) {
        await delay(remainingTime);
      }

      toast.success('Preferences saved successfully');
      setHasChanges(false);
    } catch (err) {
      captureAndReportError('Failed to save preferences:', err);
      toast.error('Failed to save preferences');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <TooltipProvider>
      <div className="w-full h-full flex flex-col">
        <div className="bg-neutral-900 h-[72px] flex-shrink-0 sticky top-0 z-40">
          <div className="h-full flex items-center justify-between px-10">
            <div className="flex items-center gap-2">
              <SettingsIcon className="size-5 fill-white" />
              <span className="text-white font-medium text-base">
                Preferences
              </span>
            </div>
            <button
              type="button"
              onClick={handleSave}
              disabled={
                !hasChanges ||
                isSaving ||
                isLoading ||
                selectedProvider === null
              }
              className={`relative bg-teal-600 text-white font-medium px-6 py-2 rounded-lg transition-all duration-300 min-w-[120px] ${
                !hasChanges ||
                isSaving ||
                isLoading ||
                selectedProvider === null
                  ? 'opacity-50'
                  : 'hover:bg-teal-700'
              }`}
              style={{
                cursor:
                  !hasChanges ||
                  isSaving ||
                  isLoading ||
                  selectedProvider === null
                    ? 'default'
                    : 'pointer',
              }}
              title={
                selectedProvider === null
                  ? 'Please select a storage provider'
                  : selectedProvider === 's3' && !providerConfigs.s3
                    ? 'Configure S3 before saving'
                    : undefined
              }
            >
              <span
                className={`inline-flex items-center justify-center transition-opacity duration-300 ${
                  isSaving ? 'opacity-0' : 'opacity-100'
                }`}
              >
                Save Changes
              </span>
              {isSaving && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <LoadingIcon
                    label="Saving"
                    className="size-5 text-white transition-opacity duration-300"
                  />
                </div>
              )}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-10">
            <div className="max-w-2xl mx-auto">
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <CloudUploadIcon className="size-5 fill-white" />
                  <h2 className="text-lg font-semibold text-white">
                    Upload Provider
                  </h2>
                </div>
                <p className="text-sm text-neutral-400 mb-6">
                  Choose your preferred permanent storage provider for uploading
                  NFT metadata and assets.
                </p>
              </div>

              <div className="space-y-4">
                {providers.map((provider) => {
                  const isSelected = selectedProvider === provider.id;
                  const ConfigSection = provider.ConfigSection;

                  return (
                    <div
                      key={provider.id}
                      className={`relative border rounded-xl p-4 cursor-pointer transition-all duration-200 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-neutral-800 hover:border-neutral-700 hover:bg-neutral-900/50'
                      }`}
                      onClick={() => handleProviderChange(provider.id)}
                    >
                      {provider.id === 's3' && (
                        <div className="absolute top-4 right-4 flex items-center gap-2">
                          <div className="group relative">
                            <div className="flex items-center gap-1.5 bg-blue-500/10 border border-blue-500/30 text-blue-400 px-2.5 py-1 rounded-md text-xs font-medium cursor-help">
                              <KeyIcon className="size-3.5 fill-blue-400" />
                              <span>BYOK</span>
                            </div>
                            <div className="absolute top-full right-0 mt-2 w-56 p-3 bg-neutral-800 border border-neutral-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                              <div className="text-xs text-neutral-300 space-y-2">
                                <p>
                                  🔑 <strong>Bring Your Own Keys:</strong>
                                </p>
                                <ul className="list-disc list-inside space-y-1 text-neutral-400 ml-2">
                                  <li>Use your own service credentials</li>
                                  <li>Direct uploads to your account</li>
                                  <li>Full control over your data</li>
                                  <li>No third-party storage costs</li>
                                </ul>
                              </div>
                              <div className="absolute -top-1 right-4 size-2 bg-neutral-800 border-t border-l border-neutral-700 transform rotate-45"></div>
                            </div>
                          </div>

                          <div className="group relative">
                            <div className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-2.5 py-1 rounded-md text-xs font-medium cursor-help">
                              <LockIcon className="size-3.5 fill-emerald-400" />
                              <span>Locally encrypted</span>
                            </div>
                            <div className="absolute top-full right-0 mt-2 w-64 p-3 bg-neutral-800 border border-neutral-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                              <div className="text-xs text-neutral-300 space-y-2">
                                <p>
                                  🔐{' '}
                                  <strong>Your credentials are secure:</strong>
                                </p>
                                <ul className="list-disc list-inside space-y-1 text-neutral-400 ml-2">
                                  <li>Encrypted using your wallet signature</li>
                                  <li>Stored locally in your browser only</li>
                                  <li>Never sent to our servers</li>
                                  <li>Direct browser-to-service uploads</li>
                                </ul>
                              </div>
                              <div className="absolute -top-1 right-4 size-2 bg-neutral-800 border-t border-l border-neutral-700 transform rotate-45"></div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                              isSelected
                                ? 'border-blue-500'
                                : 'border-neutral-600'
                            }`}
                          >
                            {isSelected && (
                              <div className="size-2.5 rounded-full bg-blue-500" />
                            )}
                          </div>
                        </div>
                        <div
                          className={`flex-1 ${provider.id === 's3' ? 'pr-48' : ''}`}
                        >
                          <div className="flex items-center gap-3 mb-1">
                            <img
                              src={provider.logo}
                              alt={`${provider.info.name} logo`}
                              className="h-6 w-auto object-contain"
                            />
                            <h3 className="text-base font-medium text-white">
                              {provider.info.name}
                            </h3>
                          </div>
                          <p className="text-sm text-neutral-400 mb-2">
                            {provider.info.description}
                          </p>

                          <div className="flex items-center gap-2 text-sm text-neutral-400 mb-3">
                            <AttachMoneyIcon className="size-4 fill-neutral-400" />
                            <span>{provider.info.cost}</span>
                            {provider.info.links?.map((link) => (
                              <span
                                key={link.url}
                                className="flex items-center gap-2"
                              >
                                <span className="text-neutral-600">•</span>
                                <a
                                  href={link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-400 hover:text-blue-300 transition-colors"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {link.label}
                                </a>
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      <AnimatePresence mode="wait">
                        {isSelected && (
                          <motion.div
                            key={`${provider.id}-config`}
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2, ease: 'easeInOut' }}
                            className="overflow-hidden"
                          >
                            <ConfigSection
                              isSelected={isSelected}
                              onConfigUpdate={() => {
                                setProviderConfigs({
                                  s3: hasS3Config(),
                                });
                              }}
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};
